/* eslint-disable @typescript-eslint/no-explicit-any */
import api, { getHeaders } from "@/utils/api";
import { Document } from "@/types/document";
import { Comment } from "@/types/comment";
import { Issue, NewIssue, NewIssueComment } from "@/types/issue";
import { generateFilter } from "@/utils";

export const getIssue = (id: string) =>
  new Promise((resolve, reject) => {
    api
      .get(`/tenant/tarea/${id}`, getHeaders())
      .then((res) => {
        const tarea = res.data.data.tarea;

        const attachedFiles: Document[] = [];
        const comments: Comment[] = [];

        if (tarea.adjuntos && tarea.adjuntos.length) {
          tarea.adjuntos.forEach((e: any) => {
            const splitted = e.nombre_archivo.split(".");

            attachedFiles.push({
              entityId: "",
              id: e.id,
              name: e.descripcion,
              type: splitted[splitted.length - 1],
              weight: e.peso,
            });
          });
        }

        if (tarea.comentarios && tarea.comentarios.length) {
          tarea.comentarios.forEach((comentario: any) => {
            const attachedFilesComment: Document[] = [];

            if (comentario.adjuntos && comentario.adjuntos.length) {
              comentario.adjuntos.forEach((adjuntoComment: any) => {
                attachedFilesComment.push({
                  entityId: "",
                  id: adjuntoComment.id,
                  name: adjuntoComment.nombre,
                  type: adjuntoComment.adjunto_tipo_id,
                  weight: adjuntoComment.file_size,
                });
              });

            }

            comments.push({
              id: comentario.id,
              text: comentario.comentario,
              fecha: comentario.fecha,
              email: comentario.ejecutivo.email,
              attachedFiles: attachedFilesComment,
            });
          });
        }

        const issue = new Issue(
          id,
          tarea.folio,
          tarea.titulo,
          tarea.descripcion,
          tarea.estado_text,
          tarea.estado_id,
          tarea.tipo_id,
          tarea.tipo_text,
          tarea.fecha,
          tarea.created_at,
          tarea.updated_at,
          tarea.adjuntos,
          comments
        );

        resolve(issue);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const getIssues = (contrato: string, page = 1) =>
  new Promise((resolve, reject) => {
    let filterToApply = "";

    if (page) {
      filterToApply += "&page=" + page;
    }

    filterToApply += generateFilter("tarea_tenant_filter", {
      contrato: contrato,
    });

    api
      .get(`/tenant/tareas?${filterToApply}`, getHeaders())
      .then((res) => {
        let issues: Issue[] = [];

        const tareas = res.data.data.tareas;
        if (tareas === undefined) {
          resolve({
            issues,
            pager: {
              page_total: 0,
            },
          });
        }

        res.data.data.tareas.forEach(
          ({
            id,
            folio,
            titulo,
            descripcion,
            estado_text,
            estado_id,
            tipo_text,
            tipo_id,
            adjuntos,
            comentarios,
            fecha,
            created_at,
            updated_at,
          }: any) => {
            const attachedFiles: Document[] = [];
            const comments: Comment[] = [];

            if (adjuntos && adjuntos.length) {
              adjuntos.forEach((e: any) => {
                const splitted = e.nombre_archivo.split(".");

                attachedFiles.push({
                  entityId: "",
                  id: e.id,
                  name: e.descripcion,
                  type: splitted[splitted.length - 1],
                  weight: e.peso,
                });
              });
            }

            if (comentarios && comentarios.length) {
              comentarios.forEach((comentario: any) => {
                const attachedFilesComment: Document[] = [];

                if (comentario.adjuntos && comentario.adjuntos.length) {
                  comentario.adjuntos.forEach((adjuntoComment: any) => {
                    attachedFilesComment.push({
                      entityId: "",
                      id: adjuntoComment.id,
                      name: adjuntoComment.nombre,
                      type: adjuntoComment.adjunto_tipo_id,
                      weight: adjuntoComment.file_size,
                    });
                  });
                }

                comments.push({
                  id: comentario.id,
                  text: comentario.comentario,
                  fecha: comentario.fecha,
                  email: comentario.ejecutivo.email,
                  attachedFiles: attachedFilesComment,
                });
              });
            }

            const issue = new Issue(
              id,
              folio,
              titulo,
              descripcion,
              estado_text,
              estado_id,
              tipo_id,
              tipo_text,
              fecha,
              created_at,
              updated_at,
              adjuntos,
              comments
            );
            issues.push(issue);
          }
        );

        const pageTotal = res.data.data.pager.page_total;
        if (page > pageTotal) {
          issues = [];
        }

        resolve({ issues, pager: res.data.data.pager });
      })
      .catch((err) => {
        reject(err);
      });
  });

export const downloadTareaImagen = (imageId: string, tareaId: string) =>
  new Promise((resolve, reject) => {
    api
      .get(
        `/tenant/tareas/${tareaId}/adjuntos/${imageId}/download`,
        getHeaders()
      )
      .then((res: any) => {
        resolve(res.data.data.file_data);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const createIssue = (
  newIssue: NewIssue
): Promise<{ error: boolean; data: any }> =>
  new Promise((resolve, reject) => {
    api
      .post(`/tenant/tareas/crear`, newIssue, getHeaders())
      .then((res) => {
        resolve({
          error: false,
          data: null,
        });
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          resolve({
            error: true,
            data: error.response.data.data,
          });
        } else {
          reject(error);
        }
      });
  });

export const createIssueComment = (
  issueId: string,
  newIssueComment: NewIssueComment
): Promise<{ error: boolean; data: any }> =>
  new Promise((resolve, reject) => {
    api
      .post(
        `/tenant/tareas/${issueId}/comentarios`,
        newIssueComment,
        getHeaders()
      )
      .then((res: any) => {
        resolve({
          error: false,
          data: res,
        });
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          resolve({
            error: true,
            data: error.response.data.data,
          });
        } else {
          reject(error);
        }
      });
  });

export function recorrerErrores(valores: any): string {
  let result = "";
  valores.forEach((item: any) => {
    result += "[* " + item.detail + "\n]";
  });
  return result;
}
